/**
 * EventDetailPopover -- centered modal popover showing event details.
 *
 * More compact than a full slide-over, doesn't take over the entire screen.
 * Closes on Escape key, backdrop click, or close button.
 */

import { useEffect, useRef } from "react";
import type { SidebarEvent } from "../layout/sidebar/types";

interface EventDetailPopoverProps {
  event: SidebarEvent | null;
  isOpen: boolean;
  onClose: () => void;
}

const EVENT_TYPE_COLORS: Record<string, string> = {
  meeting: "bg-primary-subtle text-primary",
  appointment: "bg-[rgba(168,85,247,0.12)] text-[#a855f7]",
  deadline: "bg-[rgba(239,68,68,0.12)] text-error",
  reminder: "bg-[rgba(234,179,8,0.12)] text-[#eab308]",
  focus: "bg-[rgba(34,197,94,0.12)] text-success",
};

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDuration(startStr: string, endStr: string): string {
  const start = new Date(startStr);
  const end = new Date(endStr);
  const diffMs = end.getTime() - start.getTime();
  const diffMins = Math.round(diffMs / (1000 * 60));

  if (diffMins < 60) return `${diffMins} min`;
  const hours = Math.floor(diffMins / 60);
  const mins = diffMins % 60;
  if (mins === 0) return `${hours} hr`;
  return `${hours} hr ${mins} min`;
}

export function EventDetailPopover({
  event,
  isOpen,
  onClose,
}: EventDetailPopoverProps): React.ReactElement {
  const panelRef = useRef<HTMLDivElement>(null);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(e: KeyboardEvent): void {
      if (e.key === "Escape") {
        onClose();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Focus trap - focus the panel when opened
  useEffect(() => {
    if (isOpen && panelRef.current) {
      panelRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen || !event) {
    return <></>;
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-50"
        onClick={onClose}
      />

      {/* Popover panel */}
      <div
        ref={panelRef}
        tabIndex={-1}
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90vw] max-w-sm bg-surface border border-border rounded-xl shadow-lg focus:outline-none"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-2.5 h-2.5 rounded-full bg-primary flex-shrink-0" />
            <h2 className="text-sm font-semibold text-text-primary leading-tight truncate">
              {event.title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-md text-text-tertiary hover:text-text-primary hover:bg-surface-hover transition-colors duration-100 flex-shrink-0 -mr-1 -mt-1"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-3 space-y-3 max-h-[50vh] overflow-y-auto">
          {/* Event type badge */}
          {event.type && (
            <span
              className={`inline-block text-xs px-2 py-0.5 rounded capitalize ${EVENT_TYPE_COLORS[event.type.toLowerCase()] ?? "bg-surface-active text-text-secondary"}`}
            >
              {event.type}
            </span>
          )}

          {/* Time */}
          <div className="flex items-start gap-2">
            <svg
              className="w-4 h-4 text-text-tertiary mt-0.5 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="text-sm">
              {event.allDay ? (
                <span className="text-text-primary">All day</span>
              ) : (
                <div>
                  <span className="text-text-primary">{formatDate(event.startTime)}</span>
                  <span className="text-text-secondary"> at </span>
                  <span className="text-text-primary">{formatTime(event.startTime)}</span>
                  {event.endTime && (
                    <span className="text-text-tertiary ml-1">
                      ({formatDuration(event.startTime, event.endTime)})
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Location */}
          {event.location && (
            <div className="flex items-start gap-2">
              <svg
                className="w-4 h-4 text-text-tertiary mt-0.5 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
                />
              </svg>
              <span className="text-sm text-text-secondary">{event.location}</span>
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="px-4 py-2.5 border-t border-border">
          <p className="text-[11px] text-text-tertiary text-center">
            Use AI to edit or delete this event
          </p>
        </div>
      </div>
    </>
  );
}
